import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';

// =============================================================================
// Kiosk page — shared-Web-Clip pairing model
// (docs/kiosk-shared-webclip-pairing-spec.md).
//
// URL: `/kiosk` — no params. The page reads `wic-kiosk-device-token` from
// localStorage; if absent, POST /api/kiosk/claim mints one and stores it.
// Then GET /api/kiosk/resolve loops to learn whether this device is unbound
// (holding screen + claim code), bound (renders the room), or revoked
// ("Deauthorized — contact IT" screen).
//
// All API auth uses X-Device-Token; the iPad never holds a room key. The
// backend maps token → bindings doc → roomId → room mailbox internally.
// =============================================================================

const TOKEN_STORAGE_KEY = 'wic-kiosk-device-token';
const APP_VERSION = '0.0.1';

const RESOLVE_POLL_UNBOUND_MS = 6_000;     // spec: ~5-10s; tight enough for admin/admin to flip the screen fast
const RESOLVE_POLL_BOUND_MS = 2 * 60_000;  // spec: "every few minutes"
const STATUS_POLL_MS = 30_000;

// ---- API shapes ------------------------------------------------------------

interface KioskStatus {
  room: { roomId: string; displayName: string; capacity?: number; timeZone: string };
  asOf: string;
  now: { subject: string; organizer: string; start: string; end: string } | null;
  next: { subject: string; start: string; end: string } | null;
  upcoming: Array<{ subject: string; start: string; end: string; organizer?: string }>;
  pendingCommand?: 'reload' | 'blank' | 'clear';
}

interface ResolveResponse {
  status: 'unbound' | 'bound' | 'revoked';
  claimCode: string;
  roomId: string | null;
  room: { roomId: string; displayName: string; timeZone: string; capacity?: number } | null;
  pendingCommand?: 'reload' | 'blank' | 'clear' | null;
}

interface ClaimResponse {
  deviceToken: string;
  claimCode: string;
  status: 'unbound' | 'bound' | 'revoked';
  roomId: string | null;
}

interface BookResponse  { eventId: string; subject: string; start: string; end: string; timeZone: string }
interface EndResponse   { eventId: string; newEnd: string; timeZone: string }
interface ExtendResponse extends EndResponse {}

interface ApiError extends Error { code?: string; status?: number; details?: unknown }

function authHeaders(token: string): Record<string, string> {
  return { 'X-Device-Token': token, 'X-App-Version': APP_VERSION };
}

// ---- Constants / display helpers ------------------------------------------

const GUEST_WIFI_QR = 'WIFI:T:WPA;S:WIC Guest;P:3033713714;H:false;;';
const BOOK_DURATIONS = [15, 30, 60] as const;
const EXTEND_DURATIONS = [15, 30] as const;

// iPad full-screen safe-area fix — the kiosk page renders edge-to-edge by
// painting `body` with the same color the `.kiosk` panel currently shows.
// Without this, iOS standalone WKWebView paints the status-bar strip + the
// bottom-edge area with the body's default white, producing visible white
// bars on a home-button iPad. styles.css has the body[data-kiosk-state=…]
// selectors; this hook just toggles the attribute. Cleared on unmount so
// the management UI stays white-themed.
function useKioskBodyState(state: string | null) {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!state) {
      delete document.body.dataset.kioskState;
      return;
    }
    document.body.dataset.kioskState = state;
    return () => { delete document.body.dataset.kioskState; };
  }, [state]);
}

function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function fmtTimeInZone(iso: string, _zone: string): string {
  // Graph already returned the time in the room's zone. ISO has no offset,
  // so render HH:MM directly — no Intl conversion needed.
  const m = /T(\d{2}):(\d{2})/.exec(iso);
  if (!m) return iso;
  const h = Number(m[1]);
  const mm = m[2];
  const ampm = h >= 12 ? 'p' : 'a';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${mm}${ampm}`;
}

function secondsAgo(epochMs: number): number {
  if (!epochMs) return 0;
  return Math.max(0, Math.round((Date.now() - epochMs) / 1000));
}

function freeWindowLabel(data: KioskStatus, tz: string): string {
  if (data.upcoming.length === 0) return 'Free for the rest of the day';
  return `Free until ${fmtTimeInZone(data.upcoming[0].start, tz)}`;
}

// ---- Token persistence (the spec's "single most important thing to test") -

function readStoredToken(): string | null {
  try { return window.localStorage.getItem(TOKEN_STORAGE_KEY); }
  catch { return null; }
}

function writeStoredToken(token: string): void {
  try { window.localStorage.setItem(TOKEN_STORAGE_KEY, token); }
  catch { /* private mode / disabled localStorage — best-effort */ }
}

function clearStoredToken(): void {
  try { window.localStorage.removeItem(TOKEN_STORAGE_KEY); }
  catch { /* ignore */ }
}

// ---- API fetchers ---------------------------------------------------------

async function fetchKioskStatus(token: string): Promise<KioskStatus> {
  const res = await fetch('/api/kiosk/status', { headers: authHeaders(token) });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`);
  }
  return (await res.json()) as KioskStatus;
}

async function postKiosk<T>(token: string, action: 'book' | 'end' | 'extend', body?: object): Promise<T> {
  const res = await fetch(`/api/kiosk/${action}`, {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) {
    let parsed: { error?: string; details?: unknown } = {};
    try { parsed = await res.json(); } catch { /* ignore */ }
    const err: ApiError = new Error(humanizeError(parsed.error, res.status));
    err.code = parsed.error;
    err.status = res.status;
    err.details = parsed.details;
    throw err;
  }
  return (await res.json()) as T;
}

async function postClaim(existingToken: string | null): Promise<ClaimResponse> {
  const headers: Record<string, string> = { 'X-App-Version': APP_VERSION };
  if (existingToken) headers['X-Device-Token'] = existingToken;
  const res = await fetch('/api/kiosk/claim', { method: 'POST', headers });
  if (!res.ok) throw new Error(`claim HTTP ${res.status}`);
  return (await res.json()) as ClaimResponse;
}

async function getResolve(token: string): Promise<ResolveResponse> {
  const res = await fetch('/api/kiosk/resolve', {
    headers: { 'X-Device-Token': token, 'X-App-Version': APP_VERSION },
  });
  if (res.status === 401) {
    // Server doesn't know this token. Surface as 'unbound' so the front-
    // controller drops the stored token and re-claims on next render.
    const err: ApiError = new Error('invalid_device_token');
    err.code = 'invalid_device_token';
    err.status = 401;
    throw err;
  }
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`resolve HTTP ${res.status}: ${body.slice(0, 200)}`);
  }
  return (await res.json()) as ResolveResponse;
}

function humanizeError(code: string | undefined, status: number): string {
  switch (code) {
    case 'conflict':                return 'Room booked during that window — try a shorter slot.';
    case 'extend_conflict':         return 'Next meeting starts too soon — can\'t extend.';
    case 'invalid_duration':        return 'Pick 15, 30, or 60 minutes.';
    case 'invalid_extension':       return 'Extension must be 15 or 30 minutes.';
    case 'no_in_progress_meeting':  return 'No meeting is in progress.';
    case 'missing_device_token':
    case 'invalid_device_token':    return 'This kiosk is not authorized.';
    case 'binding_revoked':         return 'This kiosk has been deauthorized.';
    case 'binding_unbound':         return 'This kiosk has no room assigned yet.';
    case 'booking_unverified':
    case 'end_unverified':
    case 'extend_unverified':       return 'Write completed but readback failed — check Outlook before retrying.';
    case 'graph_calendar_failed':
    case 'booking_write_failed':
    case 'end_write_failed':
    case 'extend_write_failed':     return 'Couldn\'t reach the calendar. Try again in a moment.';
    default:                        return code ?? `HTTP ${status}`;
  }
}

// =========================================================================
// Top-level — claim-on-first-touch / resolve cycle owns the token state.
// =========================================================================

export function Kiosk() {
  const [token, setToken] = useState<string | null>(() => readStoredToken());
  const [claimCode, setClaimCode] = useState<string | null>(null);
  const [claimError, setClaimError] = useState<string | null>(null);
  const clock = useClock();

  // 1) If we don't have a token yet, claim one and store it.
  useEffect(() => {
    if (token) return;
    let cancelled = false;
    void (async () => {
      try {
        const resp = await postClaim(null);
        if (cancelled) return;
        writeStoredToken(resp.deviceToken);
        setToken(resp.deviceToken);
        setClaimCode(resp.claimCode);
      } catch (e) {
        if (cancelled) return;
        setClaimError((e as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // 2) With a token, run /resolve on a cadence that depends on bound state.
  const resolveQ = useQuery({
    queryKey: ['kiosk-resolve', token],
    queryFn: () => getResolve(token!),
    enabled: !!token,
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      // bound → slow poll for rebind/revoke; revoked → same slow poll so an
      // admin un-revoking flips the screen back without a physical visit.
      if (status === 'bound' || status === 'revoked') return RESOLVE_POLL_BOUND_MS;
      // unbound or unknown → fast poll so admin's "Assign room" flips the
      // holding screen within seconds.
      return RESOLVE_POLL_UNBOUND_MS;
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
    retry: 2,
  });

  // 3) If /resolve returned 401 (token unknown to the server — wiped from
  // Cosmos? rolled?), drop the local token and re-claim on the next render.
  useEffect(() => {
    if (!resolveQ.error) return;
    const err = resolveQ.error as ApiError;
    if (err.code === 'invalid_device_token' || err.status === 401) {
      clearStoredToken();
      setToken(null);
      setClaimCode(null);
    }
  }, [resolveQ.error]);

  // 4) Handle `reload` pendingCommand from /resolve. The server consumes the
  // command atomically on both /resolve and /status; whichever poll lands
  // first wins. When /status is broken (Graph down etc.) /resolve is the
  // ONLY channel admin reloads can reach the kiosk through, so we have to
  // act on it here too. blank/clear are local-state inside KioskRoom and
  // moot when status is broken (kiosk already shows the error screen).
  useEffect(() => {
    if (resolveQ.data?.pendingCommand === 'reload') {
      window.location.reload();
    }
  }, [resolveQ.data?.pendingCommand]);

  // Holding screen — claim in flight, no token yet.
  if (!token) {
    return <UnclaimedScreen clock={clock} claimCode={null} error={claimError} />;
  }

  // Loading the very first /resolve — show holding screen with the claim
  // code from the claim response if we have it, otherwise neutral.
  if (!resolveQ.data) {
    return <UnclaimedScreen clock={clock} claimCode={claimCode} error={null} />;
  }

  const resolved = resolveQ.data;

  if (resolved.status === 'unbound') {
    return <UnclaimedScreen clock={clock} claimCode={resolved.claimCode} error={null} />;
  }
  if (resolved.status === 'revoked') {
    return <RevokedScreen clock={clock} claimCode={resolved.claimCode} />;
  }

  // bound — render the room. Pending commands flow through the room view's
  // status poll (kiosk-status.ts returns pendingCommand on /status as well
  // as /resolve), so we don't double-handle them here.
  return <KioskRoom token={token} claimCode={resolved.claimCode} />;
}

// =========================================================================
// Holding screens — the equivalent of the old "Missing device key" page.
// Big, branded, glance-readable.
// =========================================================================

function UnclaimedScreen({
  clock, claimCode, error,
}: { clock: Date; claimCode: string | null; error: string | null }) {
  useKioskBodyState('unclaimed');
  return (
    <div className="kiosk kiosk-unclaimed">
      <KioskHeader clock={clock} />
      <section className="kiosk-name-bar">
        <h1 className="kiosk-room-name">Unclaimed kiosk</h1>
        <p className="kiosk-room-meta">Assign a room in <code>/admin/devices</code></p>
      </section>
      <main className="kiosk-main">
        <div className="kiosk-claimcode">
          <div className="kiosk-claimcode-label">Device</div>
          <div className="kiosk-claimcode-value">
            {claimCode ?? <span className="kiosk-claimcode-placeholder">…</span>}
          </div>
        </div>
        {error ? <p className="kiosk-stub-note">Couldn't reach pairing server — retrying…</p> : null}
      </main>
      <footer className="kiosk-bottom"><div /><GuestWifiCard /></footer>
    </div>
  );
}

function RevokedScreen({ clock, claimCode }: { clock: Date; claimCode: string }) {
  useKioskBodyState('revoked');
  return (
    <div className="kiosk kiosk-revoked">
      <KioskHeader clock={clock} />
      <section className="kiosk-name-bar">
        <h1 className="kiosk-room-name">Deauthorized</h1>
        <p className="kiosk-room-meta">Contact IT — Device {claimCode}</p>
      </section>
      <main className="kiosk-main" />
      <footer className="kiosk-bottom"><div /><GuestWifiCard /></footer>
    </div>
  );
}

// =========================================================================
// Room view — what an authorized kiosk shows. Auth-source agnostic.
// =========================================================================

function KioskRoom({
  token, claimCode,
}: { token: string; claimCode?: string }) {
  const clock = useClock();
  const queryClient = useQueryClient();
  const statusQueryKey = ['kiosk-status', token] as const;

  const { data, error, isLoading, isFetching, dataUpdatedAt } = useQuery({
    queryKey: statusQueryKey,
    queryFn: () => fetchKioskStatus(token),
    refetchInterval: STATUS_POLL_MS,
    refetchOnWindowFocus: false,
    retry: 2,
    staleTime: 0,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: statusQueryKey });

  const bookMutation = useMutation<BookResponse, ApiError, number>({
    mutationFn: (durationMinutes) => postKiosk<BookResponse>(token, 'book', { durationMinutes }),
    onSuccess: refresh,
  });
  const endMutation = useMutation<EndResponse, ApiError, void>({
    mutationFn: () => postKiosk<EndResponse>(token, 'end'),
    onSuccess: refresh,
  });
  const extendMutation = useMutation<ExtendResponse, ApiError, number>({
    mutationFn: (extensionMinutes) => postKiosk<ExtendResponse>(token, 'extend', { extensionMinutes }),
    onSuccess: refresh,
  });

  const anyMutating = bookMutation.isPending || endMutation.isPending || extendMutation.isPending;
  const lastActionError = bookMutation.error ?? endMutation.error ?? extendMutation.error ?? null;

  const endConfirm = useConfirmable();

  // Blank state persists across reloads via localStorage so an iPad that
  // power-cycles while blanked comes back blanked.
  const [blanked, setBlanked] = useState<boolean>(() => {
    try { return typeof window !== 'undefined' && window.localStorage.getItem('wic-kiosk-blanked') === '1'; }
    catch { return false; }
  });
  const setBlankedPersisted = (b: boolean) => {
    setBlanked(b);
    try {
      if (b) window.localStorage.setItem('wic-kiosk-blanked', '1');
      else   window.localStorage.removeItem('wic-kiosk-blanked');
    } catch { /* private mode etc — best-effort */ }
  };

  useEffect(() => {
    const cmd = data?.pendingCommand;
    if (!cmd) return;
    if (cmd === 'reload') {
      window.location.reload();
    } else if (cmd === 'blank') {
      setBlankedPersisted(true);
    } else if (cmd === 'clear') {
      setBlankedPersisted(false);
    }
  }, [data?.pendingCommand]);

  // Track current visual state on `body` so the iPad safe-area strips
  // (status bar / bottom edge) paint the matching color instead of white.
  const visualState =
    blanked ? 'blanked'
    : isLoading ? 'unknown'
    : (error || !data) ? 'error'
    : (data.now ? 'busy' : 'free');
  useKioskBodyState(visualState);

  if (blanked) {
    return (
      <div
        className="kiosk kiosk-blanked"
        onClick={() => setBlankedPersisted(false)}
        role="button"
        aria-label="Kiosk is blanked. Tap to wake."
      />
    );
  }

  if (isLoading) {
    return (
      <div className="kiosk kiosk-unknown">
        <KioskHeader clock={clock} />
        <section className="kiosk-name-bar">
          <h1 className="kiosk-room-name">{claimCode ?? 'Kiosk'}</h1>
          <p className="kiosk-room-meta">Loading…</p>
        </section>
        <main className="kiosk-main" />
        <footer className="kiosk-bottom"><div /><GuestWifiCard /></footer>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="kiosk kiosk-error">
        <KioskHeader clock={clock} />
        <section className="kiosk-name-bar">
          <h1 className="kiosk-room-name">Status unavailable</h1>
          <p className="kiosk-room-meta">{String((error as Error)?.message ?? 'Unknown error')}</p>
        </section>
        <main className="kiosk-main">
          <p className="kiosk-stub-note">Reconnecting on the next poll…</p>
        </main>
        <footer className="kiosk-bottom"><div /><GuestWifiCard /></footer>
      </div>
    );
  }

  const status: 'free' | 'busy' = data.now ? 'busy' : 'free';
  const tz = data.room.timeZone;

  return (
    <div className={`kiosk kiosk-${status}`}>
      <KioskHeader clock={clock} />

      <section className="kiosk-name-bar">
        <h1 className="kiosk-room-name">{data.room.displayName}</h1>
        {!data.now ? (
          <p className="kiosk-room-meta">{freeWindowLabel(data, tz)}</p>
        ) : null}
      </section>

      <main className="kiosk-main">
        {data.now ? (
          <section className="kiosk-event">
            <div className="kiosk-event-label">In progress</div>
            <div className="kiosk-event-subject">{data.now.subject}</div>
            <div className="kiosk-event-meta">
              {data.now.organizer ? `${data.now.organizer} · ` : ''}
              until {fmtTimeInZone(data.now.end, tz)}
            </div>
          </section>
        ) : null}

        <section className="kiosk-today" aria-label="Today's remaining meetings">
          <div className="kiosk-today-label">Today</div>
          {data.upcoming.length === 0 ? (
            <p className="kiosk-today-empty">No more meetings today.</p>
          ) : (
            <ul className="kiosk-today-list">
              {data.upcoming.map((evt) => {
                const showOrganizer =
                  evt.organizer &&
                  evt.organizer.trim().length > 0 &&
                  evt.organizer.trim().toLowerCase() !== evt.subject.trim().toLowerCase();
                return (
                  <li key={`${evt.start}-${evt.subject}`} className="kiosk-today-row">
                    <span className="kiosk-today-time">{fmtTimeInZone(evt.start, tz)}</span>
                    <span className="kiosk-today-meta">
                      <span className="kiosk-today-subject">{evt.subject}</span>
                      {showOrganizer ? <span className="kiosk-today-organizer"> · {evt.organizer}</span> : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </main>

      <footer className="kiosk-bottom">
        <div className="kiosk-wifi-ghost" aria-hidden="true">
          <GuestWifiCard />
        </div>

        <section className="kiosk-actions" aria-label="Room actions">
          {status === 'free' ? (
            <>
              <div className="kiosk-actions-label">Book this room</div>
              <div className="kiosk-actions-row">
                {BOOK_DURATIONS.map((min) => (
                  <button
                    key={min}
                    className="kiosk-action"
                    onClick={() => bookMutation.mutate(min)}
                    disabled={anyMutating}
                  >
                    {min} min
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="kiosk-actions-label">Adjust this meeting</div>
              <div className="kiosk-actions-row">
                {EXTEND_DURATIONS.map((min) => (
                  <button
                    key={min}
                    className="kiosk-action"
                    onClick={() => extendMutation.mutate(min)}
                    disabled={anyMutating}
                  >
                    + {min} min
                  </button>
                ))}
                <button
                  className={`kiosk-action kiosk-action-destructive${endConfirm.armed ? ' is-armed' : ''}`}
                  onClick={() => {
                    if (!endConfirm.armed) { endConfirm.arm(); return; }
                    endMutation.mutate();
                    endConfirm.disarm();
                  }}
                  disabled={anyMutating}
                >
                  {endConfirm.armed ? 'Tap again to end' : 'End early'}
                </button>
              </div>
            </>
          )}

          {lastActionError ? (
            <div className="kiosk-action-error" role="alert">
              {lastActionError.message}
            </div>
          ) : null}

          <p className="kiosk-stub-note">
            {anyMutating
              ? 'writing…'
              : isFetching
                ? 'refreshing…'
                : `refreshed ${secondsAgo(dataUpdatedAt)}s ago`}
            {import.meta.env.DEV ? <Link to="/" style={{ color: 'inherit', marginLeft: '0.75rem' }}>(dev) home</Link> : null}
          </p>
        </section>

        <GuestWifiCard />
      </footer>
    </div>
  );
}

// =========================================================================
// Shared UI bits.
// =========================================================================

function KioskHeader({ clock }: { clock: Date }) {
  return (
    <header className="kiosk-header">
      <img className="kiosk-logo" src="/brand/wic-logo-white.png" alt="WIC" width={616} height={252} />
      <div className="kiosk-timestamp">
        <div className="kiosk-clock">
          {clock.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
        </div>
        <div className="kiosk-date">
          {clock.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}
        </div>
      </div>
    </header>
  );
}

function GuestWifiCard() {
  return (
    <div className="kiosk-wifi" aria-label="Guest WiFi: scan to connect">
      <QRCodeSVG value={GUEST_WIFI_QR} size={132} level="M" bgColor="#ffffff" fgColor="#111111" />
      <span className="kiosk-wifi-label">Guest WiFi</span>
      <span className="kiosk-wifi-ssid">WIC Guest</span>
    </div>
  );
}

function useConfirmable(timeoutMs = 3000) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), timeoutMs);
    return () => clearTimeout(t);
  }, [armed, timeoutMs]);
  return {
    armed,
    arm: () => setArmed(true),
    disarm: () => setArmed(false),
  };
}
